import React, { useState, useEffect } from "react";
import {
  BarChart3,
  Lightbulb,
  TrendingUp,
  Code,
  Palette,
  Briefcase,
  Users,
  Target,
  BookOpen,
  Activity,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import CountUp from "../components/CountUp";
import styles from "./Home.module.css";

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeUsers: 5,
    skillCategories: 4,
    successRate: 90,
    totalAssessments: 5,
  });
  const [categories, setCategories] = useState([
    { name: "Technical Skills" },
    { name: "Design & Creative" },
    { name: "Business & Management" },
    { name: "Soft Skills" },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Fetching homepage data...");

        // Fetch public stats
        const statsResponse = await apiService.health.getPublicStats();
        console.log("Stats response:", statsResponse);

        if (statsResponse.success && statsResponse.stats) {
          setStats(statsResponse.stats);
          console.log("Updated stats:", statsResponse.stats);

          if (statsResponse.categories) {
            setCategories(statsResponse.categories);
            console.log("Updated categories:", statsResponse.categories);
          }
        }
      } catch (error) {
        console.error("Failed to fetch homepage data:", error);
        // Keep default fallback data
      } finally {
        setLoading(false);
        console.log("Homepage data loading complete");
      }
    };

    fetchData();
  }, []);

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (
      name.includes("technical") ||
      name.includes("programming") ||
      name.includes("development")
    ) {
      return <Code size={40} />;
    } else if (
      name.includes("design") ||
      name.includes("creative") ||
      name.includes("ui") ||
      name.includes("ux")
    ) {
      return <Palette size={40} />;
    } else if (
      name.includes("business") ||
      name.includes("management") ||
      name.includes("leadership")
    ) {
      return <Briefcase size={40} />;
    } else if (
      name.includes("soft") ||
      name.includes("communication") ||
      name.includes("team")
    ) {
      return <Users size={40} />;
    } else {
      return <Target size={40} />;
    }
  };

  const getCategoryDescription = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (
      name.includes("technical") ||
      name.includes("programming") ||
      name.includes("development")
    ) {
      return "Programming, web development, and other technical domains.";
    } else if (
      name.includes("design") ||
      name.includes("creative") ||
      name.includes("ui") ||
      name.includes("ux")
    ) {
      return "UI/UX design, graphic design, and creative skills.";
    } else if (
      name.includes("business") ||
      name.includes("management") ||
      name.includes("leadership")
    ) {
      return "Leadership, project management, and business skills.";
    } else if (
      name.includes("soft") ||
      name.includes("communication") ||
      name.includes("team")
    ) {
      return "Communication, teamwork, and other interpersonal skills.";
    } else {
      return "Specialized skills and knowledge areas.";
    }
  };

  const handleStartAssessment = (e) => {
    if (user) {
      // User is logged in, redirect to dashboard
      e.preventDefault();
      navigate("/dashboard");
    }
    // If user is not logged in, let the default anchor behavior work
  };
  return (
    <main className={styles.home}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>
              Track Your Progress. Master Your Skills.
            </h1>
            <p className={styles.heroDescription}>
              Take personalized skill assessments and get tailored learning
              paths to accelerate your professional growth with data-driven
              insights.
            </p>
            <div className={styles.heroActions}>
              <a
                href={user ? "/dashboard" : "#how-it-works"}
                className="btn btn-primary hero-btn"
                onClick={handleStartAssessment}
              >
                {user ? "Go to Dashboard" : "Start Assessment"}
                <ArrowRight size={20} style={{ marginLeft: "8px" }} />
              </a>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.heroStats}>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {loading ? (
                    "..."
                  ) : (
                    <CountUp
                      end={Math.floor(stats.activeUsers)}
                      suffix="+"
                      duration={2500}
                      delay={200}
                    />
                  )}
                </div>
                <div className={styles.statLabel}>Active Learners</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {loading ? (
                    "..."
                  ) : (
                    <CountUp
                      end={stats.skillCategories}
                      suffix="+"
                      duration={2000}
                      delay={400}
                    />
                  )}
                </div>
                <div className={styles.statLabel}>Skill Categories</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {loading ? (
                    "..."
                  ) : (
                    <CountUp
                      end={stats.successRate}
                      suffix="%"
                      duration={2200}
                      delay={600}
                    />
                  )}
                </div>
                <div className={styles.statLabel}>Success Rate</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>24/7</div>
                <div className={styles.statLabel}>Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className={styles.section}>
        <div className={styles.container}>
          <h2 className={`${styles.sectionTitle} ${styles.textCenter}`}>
            How It Works
          </h2>
          <div className={`${styles.grid} ${styles.grid3}`}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <BarChart3 size={48} />
              </div>
              <h3>1. Take Assessments</h3>
              <p>
                Complete interactive skill assessments tailored to your career
                goals.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <Lightbulb size={48} />
              </div>
              <h3>2. Get Insights</h3>
              <p>
                Receive detailed insights about your strengths and improvement
                areas.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <TrendingUp size={48} />
              </div>
              <h3>3. Follow Your Path</h3>
              <p>
                Access personalized learning resources and track your progress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skill Categories Section */}
      <section className={`${styles.section} ${styles.skillCategories}`}>
        <div className={styles.container}>
          <h2 className={`${styles.sectionTitle} ${styles.textCenter}`}>
            Skill Categories
          </h2>
          <p className={`${styles.sectionSubtitle} ${styles.textCenter}`}>
            Explore assessments across various domains
          </p>
          <div className={`${styles.grid} ${styles.grid4}`}>
            {categories.map((category, index) => (
              <div
                key={index}
                className={`${styles.categoryCard} ${
                  styles[category.name.toLowerCase().replace(/[^a-z]/g, "")]
                }`}
              >
                <div className={styles.categoryIcon}>
                  {getCategoryIcon(category.name)}
                </div>
                <h3>{category.name}</h3>
                <p>{getCategoryDescription(category.name)}</p>
                {category.count && (
                  <div className={styles.categoryCount}>
                    {category.count} assessment{category.count !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className={`${styles.section} ${styles.whyChoose}`}>
        <div className={styles.container}>
          <h2 className={`${styles.sectionTitle} ${styles.textCenter}`}>
            Why Choose ProSkillNest?
          </h2>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitItem}>
              <div className={styles.benefitIcon}>
                <Target size={40} />
              </div>
              <div className={styles.benefitContent}>
                <h3>Personalized Learning Paths</h3>
                <p>
                  Our AI-powered system creates custom learning paths based on
                  your assessment results, focusing on areas that need
                  improvement.
                </p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitIcon}>
                <BookOpen size={40} />
              </div>
              <div className={styles.benefitContent}>
                <h3>Comprehensive Skill Coverage</h3>
                <p>
                  From technical skills to soft skills like communication and
                  leadership, we cover all aspects of professional development.
                </p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitIcon}>
                <Activity size={40} />
              </div>
              <div className={styles.benefitContent}>
                <h3>Progress Tracking</h3>
                <p>
                  Monitor your growth over time with intuitive charts and
                  analytics that help you stay motivated.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
